import { server } from "../../server";
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";
import withReactContent from "sweetalert2-react-content";

function SMSTemplate() {
  const [smsTemplate, setSMSTemplate] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const MySwal = withReactContent(Swal);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const recordsPerPage = 10;
  const todayDate = new Date().toISOString().split("T")[0];

  const [formData, setFormData] = useState({
    templateId: "",
    templateName: "",
    variables: 0,
    message: "",
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.templateId || !formData.variables) {
      toast.error("Id and Variables are Required", { position: "top-right" });
      return;
    }

    try {
      const payload = {
        templateId: formData.templateId,
        templateName: formData.templateName,
        variables: formData.variables,
        message: formData.message,
      };

      // Step 5: Send final POST
      await axios.post(
        "https://7otfgyv0ke.execute-api.ap-south-1.amazonaws.com/prod/create_sms_template_mongodb",
        payload,
        {
          headers: {
            Authorization: `${localStorage.getItem("token")}`,
          },
        },
      );

      toast.success("Template Added", { position: "top-right" });

      // Step 6: Reset form
      setFormData({
        templateId: "",
        templateName: "",
        variables: null,
        message: "",
      });
    } catch (error) {
      toast.error("Please Try Again", { position: "top-right" });
    }
  };

  const fetchSMSTemplate = async () => {
    try {
      const response = await axios.post(
        `${server}/sms_template_getbydate_and_getall_mongodb`,
        {},
        {
          headers: {
            Authorization: `${localStorage.getItem("token")}`,
          },
        },
      );
      if (response.data && response.data["body-json"]?.body?.templates) {
        setSMSTemplate(response.data["body-json"].body.templates);
      } else {
        console.error("Invalid data format from API", response.data);
      }
    } catch (error) {
      console.error("Error fetching bus details:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSMSTemplate();
  }, []);

  // Filter
  const filteredRecords = smsTemplate.filter((scholarship) => {
    const searchLower = searchTerm.toLowerCase();

    return Object.values(scholarship).some((value) => {
      if (typeof value === "string") {
        return value.toLowerCase().includes(searchLower);
      } else if (typeof value === "object" && value !== null) {
        return Object.values(value).some(
          (subValue) =>
            typeof subValue === "string" &&
            subValue.toLowerCase().includes(searchLower),
        );
      }
      return false;
    });
  });

  // ✅ Reset pagination when searching
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // ✅ Apply pagination AFTER filtering
  const totalPages = Math.ceil(filteredRecords.length / recordsPerPage);
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filteredRecords.slice(
    indexOfFirstRecord,
    indexOfLastRecord,
  );

  // Page Navigation
  const nextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  const prevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const handleDelete = async (templateIdToDelete) => {
    const result = await MySwal.fire({
      title: "Are you sure?",
      text: `Do you want to delete template ID "${templateIdToDelete}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        const response = await axios.post(
          `${server}/delete_sms_template_mongodb`,
          { templateId: templateIdToDelete },
          {
            headers: {
              Authorization: `${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
          },
        );

        if (response.status === 200) {
          toast.success("Template Deleted", { position: "top-right" });

          setSMSTemplate((prev) =>
            prev.filter(
              (template) => template.templateId !== templateIdToDelete,
            ),
          );
        } else {
          toast.error("Deletion failed. Please try again.");
        }
      } catch (error) {
        toast.error("Error occurred while deleting. Try again.");
      }
    }
  };

  const handleExportAllSMSTemplatesExcel = () => {
    if (!smsTemplate || smsTemplate.length === 0) {
      toast.error("No SMS Template records to export.");
      return;
    }

    const sheetData = smsTemplate.map((item) => ({
      "Template ID": item.templateId || "",
      "Template Name": item.templateName || "",
      Variables: item.variables || 0,
      Message: item.message
        ? item.message.replace(/\\r\\n|\\n|\\r/g, "\n")
        : "",
      Date: item.createdAt
        ? new Date(item.createdAt).toLocaleDateString("en-GB")
        : "",
    }));

    const worksheet = XLSX.utils.json_to_sheet(sheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "All Templates");

    const today = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `SMSTemplates_${today}.xlsx`);
    toast.success("Report Generated");
  };

  const handleExportSMSTemplatesVariableWiseExcel = () => {
    if (!smsTemplate || smsTemplate.length === 0) {
      toast.error("No SMS Template records to export.");
      return;
    }

    const groupedByVariables = {};
    smsTemplate.forEach((item) => {
      const varCount = item.variables || 0;
      if (!groupedByVariables[varCount]) {
        groupedByVariables[varCount] = [];
      }

      groupedByVariables[varCount].push({
        "Template ID": item.templateId || "",
        "Template Name": item.templateName || "",
        Variables: varCount,
        Message: item.message
          ? item.message.replace(/\\r\\n|\\n|\\r/g, "\n")
          : "",
        Date: item.createdAt
          ? new Date(item.createdAt).toLocaleDateString("en-GB")
          : "",
      });
    });

    const workbook = XLSX.utils.book_new();

    Object.keys(groupedByVariables).forEach((varCount) => {
      const sheetData = groupedByVariables[varCount];
      const worksheet = XLSX.utils.json_to_sheet(sheetData);

      const range = XLSX.utils.decode_range(worksheet["!ref"]);
      worksheet["!autofilter"] = { ref: XLSX.utils.encode_range(range) };

      XLSX.utils.book_append_sheet(workbook, worksheet, `${varCount}`);
    });

    const today = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `SMSTemplates_VariablesWise_${today}.xlsx`);
    toast.success("Report Generated");
  };

  const handleExportSMSTemplates0VariableExcel = () => {
    if (!smsTemplate || smsTemplate.length === 0) {
      toast.error("No SMS Template records to export.");
      return;
    }

    const zeroVarTemplates = smsTemplate.filter(
      (item) => (item.variables || 0) === 0,
    );

    if (zeroVarTemplates.length === 0) {
      toast.error("No SMS Templates with 0 variables found.");
      return;
    }

    const sheetData = zeroVarTemplates.map((item) => ({
      "Template ID": item.templateId || "",
      "Template Name": item.templateName || "",
      Variables: item.variables || 0,
      Message: item.message
        ? item.message.replace(/\\r\\n|\\n|\\r/g, "\n")
        : "",
      Date: item.createdAt
        ? new Date(item.createdAt).toLocaleDateString("en-GB")
        : "",
    }));

    const worksheet = XLSX.utils.json_to_sheet(sheetData);

    const range = XLSX.utils.decode_range(worksheet["!ref"]);
    worksheet["!autofilter"] = { ref: XLSX.utils.encode_range(range) };

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Zero Variables");

    const today = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `SMSTemplates_ZeroVariables_${today}.xlsx`);

    toast.success("Report Generated");
  };

  return (
    <>
      <div id="db-wrapper">
        <main id="page-content">
          <div
            class="modal fade gd-example-modal-lg"
            tabindex="-1"
            role="dialog"
            aria-labelledby="myLargeModalLabel"
            aria-hidden="true"
          >
            <div class="modal-dialog modal-lg">
              <div class="modal-content">
                <form onSubmit={handleSubmit}>
                  <div className="modal-header d-flex justify-content-between">
                    <h5
                      className="modal-title fs-4"
                      id="exampleModalToggleLabel"
                    >
                      SMS Template
                    </h5>
                    <div className="d-flex align-items-center">
                      <span className="badge bg-success-soft me-3 px-3">
                        Editable
                      </span>
                      <div className="form-check form-switch me-4">
                        <input
                          type="checkbox"
                          className="form-check-input m-0"
                          checked
                          readOnly
                        />
                      </div>
                      <button
                        type="button"
                        className="btn-close"
                        data-bs-dismiss="modal"
                        aria-label="Close"
                      ></button>
                    </div>
                  </div>

                  <div className="modal-body">
                    <div className="row g-2 mb-2">
                      {/* Template Id */}
                      <div className="col-lg-6 col-12">
                        <label className="form-label mb-1 ms-3">
                          Template Id <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control text-dark"
                          name="templateId"
                          value={formData.templateId}
                          onChange={handleChange}
                          placeholder="Enter Template ID"
                          required
                        />
                      </div>

                      {/* Template Name */}
                      <div className="col-lg-6 col-12">
                        <label className="form-label mb-1 ms-3">
                          Template Name
                        </label>
                        <input
                          type="text"
                          className="form-control text-dark"
                          name="templateName"
                          value={formData.templateName}
                          onChange={handleChange}
                          placeholder="Enter Template Name"
                          required
                        />
                      </div>

                      {/* Variables */}
                      <div className="col-lg-6 col-12">
                        <label className="form-label mb-1 ms-3">
                          Variables <span className="text-danger">*</span>
                        </label>
                        <input
                          type="number"
                          className="form-control text-dark"
                          name="variables"
                          value={formData.variables}
                          onChange={handleChange}
                          placeholder="Enter Variables"
                          required
                        />
                      </div>

                      {/* Message */}
                      <div className="col-12">
                        <label className="form-label mb-1 ms-3">
                          Message <span className="text-danger">*</span>
                        </label>
                        <textarea
                          className="form-control text-dark"
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          placeholder="Enter Message"
                          rows="4"
                          required
                        ></textarea>
                      </div>
                    </div>
                  </div>

                  <div className="modal-footer d-flex justify-content-between py-2">
                    <div>
                      <button
                        type="button"
                        className="btn btn-secondary px-3 me-1 rounded-pill"
                        data-bs-dismiss="modal"
                      >
                        <i className="bi bi-x-lg"></i>
                      </button>
                      <button
                        type="submit"
                        class="btn btn-primary px-5 rounded-pill fw-normal"
                        disabled={loading}
                      >
                        {loading ? (
                          <div
                            class="spinner-border spinner-border-sm"
                            role="status"
                          ></div>
                        ) : (
                          "Submit"
                        )}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>

          <div className="">
            <div class="container-fluid my-3 px-3">
              <div className="row g-2">
                <div className="col-lg-8">
                  <div class="table-responsive border-0 overflow-y-hidden bg-white rounded-4">
                    <table class="table mb-0 text-nowrap table-centered">
                      <thead class="table-light">
                        <tr>
                          <th className="w-10">No.</th>
                          <th className="lh-1 w-60">
                            Details
                            <i className="bi bi-info-circle ms-2 text-dark"></i>
                            <br />
                            <span className="fs-8 fw-normal text-secondary">
                              Displaying Information as per Record
                            </span>
                          </th>
                          <th className="lh-1 w-30">
                            Action
                            <i className="bi bi-person-gear ms-2 text-dark"></i>
                            <br />
                            <span className="fs-8 fw-normal text-secondary">
                              Role-Specific Action Panel
                            </span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentRecords.map((item, index) => (
                          <tr>
                            <td>{indexOfFirstRecord + index + 1}.</td>
                            <td className="lh-sm py-4 text-wrap">
                              <span class="text-dark">Template Id</span> &mdash;{" "}
                              {item.templateId}
                              <br />
                              <span class="text-dark">Template Name</span>{" "}
                              &mdash; {item.templateName}
                              <br />
                              <span class="text-dark">
                                Variables
                              </span> &mdash; {item.variables}
                              <br />
                              <span class="text-dark">Message</span> &mdash;
                              <div>
                                {item.message
                                  .replace(/\\r\\n|\\n|\\r/g, "\n")
                                  .split("\n")
                                  .flatMap((line, lineIndex) =>
                                    line
                                      .split(" ")
                                      .reduce((acc, word, index) => {
                                        const groupIndex = Math.floor(
                                          index / 7,
                                        );
                                        if (!acc[groupIndex])
                                          acc[groupIndex] = [];
                                        acc[groupIndex].push(word);
                                        return acc;
                                      }, [])
                                      .map((group, groupIndex) => (
                                        <span
                                          key={`${lineIndex}-${groupIndex}`}
                                        >
                                          {group.join(" ")}
                                          <br />
                                        </span>
                                      )),
                                  )}
                              </div>
                              <br />
                              <span class="text-dark">Date</span> &mdash;{" "}
                              {item.createdAt
                                ? new Date(item.createdAt).toLocaleDateString(
                                    "en-GB",
                                  )
                                : ""}
                            </td>
                            <td>
                              <div className="d-flex flex-column gap-1 width-fit-content">
                                <span class="badge bg-success-soft cursor-pointer px-4 rounded-3 btn-py-sm">
                                  Update
                                </span>
                                <span
                                  onClick={() => handleDelete(item.templateId)}
                                  class="badge bg-danger-soft cursor-pointer px-4 rounded-3 btn-py-sm"
                                >
                                  Delete
                                </span>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="col-lg-4">
                  <div class="">
                    <div className="mb-3">
                      <div className="d-flex bg-white justify-content-between rounded-4">
                        <form
                          className="d-flex align-items-center"
                          onSubmit={(e) => e.preventDefault()}
                        >
                          <span className="position-absolute ps-3 search-icon">
                            <i className="bi bi-filter-circle-fill ms-1" />
                          </span>
                          <input
                            type="search"
                            className="form-control ps-7 rounded-3 border-0 rounded-4"
                            placeholder="Filter Data"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                          />
                        </form>
                        <Link
                          data-bs-toggle="modal"
                          data-bs-target=".gd-example-modal-lg"
                          class="btn btn-warning px-5 fs-6 fw-medium rounded-4 fit-content"
                        >
                          Add Template
                        </Link>
                      </div>
                      <div className="mt-2 ms-4">
                        <nav aria-label="breadcrumb">
                          <ol class="breadcrumb pe-3">
                            <li class="breadcrumb-item">
                              <a href="#">Communicate</a>
                            </li>
                            <li
                              class="breadcrumb-item active"
                              aria-current="page"
                            >
                              SMS Template
                            </li>
                          </ol>
                        </nav>
                      </div>
                    </div>

                    <div className="p-4 bg-white rounded-4">
                      <div className="d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center">
                          {loading ? (
                            <div
                              className="spinner-border text-dark data-loader-spinner"
                              role="status"
                            ></div>
                          ) : (
                            <img src="/assets/images/folder.svg" />
                          )}

                          <p className="my-0 fw-semibold lh-1 fs-5 text-dark mx-3">
                            SMS Template
                            <br />
                            <span className="text-secondary fs-6 fw-medium lh-sm">
                              0.2 MB Size
                            </span>
                          </p>
                        </div>
                        <span className="badge bg-pink rounded-3 px-4 py-2 me-1 mt-1 cursor-pointer">
                          Export
                        </span>
                      </div>

                      <div class="px-4 py-2 mt-3 bg-light rounded-4">
                        <li className="list-group-item px-0">
                          <a
                            className="d-flex align-items-center text-inherit mb-0 justify-content-between"
                            data-bs-toggle="collapse"
                            href="#specifications"
                            role="button"
                            aria-expanded="false"
                            aria-controls="specifications"
                          >
                            <p className="m-0 fw-medium lh-sm text-dark">
                              Download Report
                            </p>
                            <span className="chevron-arrow ms-4">
                              <i className="bi bi-chevron-down fs-4" />
                            </span>
                          </a>
                          <div
                            className="collapse"
                            id="specifications"
                            data-bs-parent="#ecommerceAccordion"
                          >
                            <div className="py-3">
                              <div class="rounded-4 d-flex flex-wrap">
                                <span
                                  onClick={handleExportAllSMSTemplatesExcel}
                                  class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                                >
                                  All Templates
                                </span>
                                <span
                                  onClick={
                                    handleExportSMSTemplatesVariableWiseExcel
                                  }
                                  class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                                >
                                  Variable Wise
                                </span>
                                <span
                                  onClick={
                                    handleExportSMSTemplates0VariableExcel
                                  }
                                  class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                                >
                                  Templates With 0 Variable
                                </span>
                              </div>
                            </div>
                          </div>
                        </li>
                      </div>
                    </div>

                    <div className="my-4">
                      <nav>
                        <ul className="pagination justify-content-center mb-0">
                          <li
                            className={`page-item ${
                              currentPage === 1 ? "disabled" : ""
                            }`}
                          >
                            <button className="page-link" onClick={prevPage}>
                              <i className="bi bi-chevron-left"></i>
                            </button>
                          </li>

                          {(() => {
                            const pageNumbers = [];
                            pageNumbers.push(1);

                            if (currentPage > 3) {
                              pageNumbers.push("left-ellipsis");
                            }

                            if (
                              currentPage !== 1 &&
                              currentPage !== totalPages
                            ) {
                              pageNumbers.push(currentPage);
                            }

                            if (currentPage < totalPages - 2) {
                              pageNumbers.push("right-ellipsis");
                            }

                            if (totalPages > 1) {
                              pageNumbers.push(totalPages);
                            }

                            return pageNumbers.map((page, index) => {
                              if (
                                page === "left-ellipsis" ||
                                page === "right-ellipsis"
                              ) {
                                return (
                                  <li
                                    key={index}
                                    className="page-item disabled"
                                  >
                                    <span className="page-link">...</span>
                                  </li>
                                );
                              }

                              return (
                                <li
                                  key={index}
                                  className={`page-item ${
                                    currentPage === page ? "active" : ""
                                  }`}
                                >
                                  <button
                                    className="page-link"
                                    onClick={() => setCurrentPage(page)}
                                  >
                                    {page}
                                  </button>
                                </li>
                              );
                            });
                          })()}

                          <li
                            className={`page-item ${
                              currentPage === totalPages ? "disabled" : ""
                            }`}
                          >
                            <button className="page-link" onClick={nextPage}>
                              <i className="bi bi-chevron-right"></i>
                            </button>
                          </li>
                        </ul>
                      </nav>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
}

export default SMSTemplate;
